import { tutorOneOffBooking } from "./tutorOneOffBooking.js";
import { useState } from "react";
import { ActionConfirmation, type ActionOutcome } from "../ui/ActionConfirmation.js";
import { useAsync } from "../useAsync.js";
import {
  describeTeachingLinkRequest,
  getMyTeachingLinks,
  requestTeachingLinkFromHistory,
} from "./teachingLinks.js";

/**
 * A tutor's own teaching links for a pupil, and the way to get one.
 *
 * Booking a single lesson or selling a standard block needs an active link.
 * This shows the tutor which links they hold and lets them apply the
 * owner-approved lesson-history rule to this pupil; what the rule cannot
 * decide goes to the owners. Owners manage every teacher from the
 * Teachers panel instead.
 */
export function TeachingLinkHelp({
  studentId,
  studentName,
  bookingBlocked = false,
  onLinked,
}: {
  studentId: string;
  studentName: string;
  /** Shown where a booking has just been refused for want of a link. */
  bookingBlocked?: boolean;
  onLinked?: () => void;
}) {
  const links = useAsync(() => getMyTeachingLinks(studentId), [studentId]);
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<ActionOutcome | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  async function ask() {
    if (busy) return;
    setBusy(true);
    setProblem(null);
    try {
      const result = await requestTeachingLinkFromHistory(studentId);
      const summary = describeTeachingLinkRequest(result);
      setOutcome({
        variant: summary.variant,
        title: summary.title,
        description: studentName,
        details: summary.details.map((value) => ({ value })),
      });
      links.reload();
      if (result.linked.length > 0) {
        tutorOneOffBooking.invalidateOfferings(studentId);
        onLinked?.();
      }
    } catch (cause: unknown) {
      setProblem(cause instanceof Error ? cause.message : "The request could not be sent. Nothing was changed.");
    } finally {
      setBusy(false);
    }
  }

  const held = links.data?.links ?? [];
  return (
    <div className="panel">
      <h3 style={{ marginTop: 0 }}>{bookingBlocked ? "Book a lesson" : "Your teaching links"}</h3>
      {bookingBlocked && (
        <p className="small muted">
          You are not linked to {studentName} for a lesson type you can book yet. Booking a lesson
          or selling a block needs a teaching link.
        </p>
      )}
      {!bookingBlocked && links.loading && <p className="empty">Loading your links…</p>}
      {!bookingBlocked && !links.loading && held.length === 0 && (
        <p className="small muted">You are not linked to this pupil for any instrument.</p>
      )}
      {held.length > 0 && (
        <ul className="small" style={{ marginTop: 0 }}>
          {held.map((link) => (
            <li key={`${link.instrument ?? link.serviceName ?? "lesson"}:${link.startsAt}`}>
              {link.instrument ?? link.serviceName ?? "Lessons"}
            </li>
          ))}
        </ul>
      )}
      {outcome && <ActionConfirmation {...outcome} />}
      {problem && <div className="error">{problem}</div>}
      <button type="button" className={bookingBlocked ? undefined : "quiet small"} disabled={busy} onClick={() => void ask()}>
        {busy ? "Checking your lessons…" : "Link me from my lesson history"}
      </button>
      <p className="small muted" style={{ marginBottom: 0 }}>
        Uses your confirmed lessons with this pupil. If an owner needs to decide, they are told. Nothing is booked or charged.
      </p>
    </div>
  );
}
